import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import logo from "../../../../assets/images/image.png";
import bgImage from "../../../../assets/images/bgImage.png";
import { Input } from "../../(components)/ui/input";
import { StyledButton, buttonStyles } from "../../../../Shared/button/button";
import ToastSuccess from "../../../(common)/Toast/ToastSuccess";
import ToastWarning from "../../../(common)/Toast/ToastWarning";
import { callApi } from "../../lib/api";
import { passwordCriteria } from "../../constants/validationRules";
import CategoryDropdown from "../SignUp/(components)/CategoryDropdown";
import authStyles from "../withAuth.module.scss";
import { getSubCategoryOptions } from "../../../../utils/newOnboarding/utilities";
import { testIdProps } from "../../../../utils/testIdProps";
import {
  getCurrentOrganisationId,
  getUserDisplayEmail,
  getUserDisplayName,
  syncStoredOrganisationId,
} from "../../../../utils/authSession";
import { logout } from "../../../../utils/logout";

type CategoryType = { category_pid: string; category: string };

const schema = Yup.object({
  businessName: Yup.string()
    .trim()
    .required("Business name is required")
    .min(3, "Minimum 3 chars allowed")
    .max(50, "Maximum 50 chars allowed")
    .matches(/^[a-zA-Z0-9 ]*$/, "The field should not contain special characters"),
  category: Yup.object({
    category_pid: Yup.string().trim().required("Please select one category"),
    category: Yup.string().trim().required("Please select one category"),
  }).required("Please select one category"),
});

function readStoredUser(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem("@user") || "{}");
  } catch {
    return {};
  }
}

const lockedFieldStyle: React.CSSProperties = {
  color: "#6c757d",
  WebkitTextFillColor: "#6c757d",
  cursor: "not-allowed",
};

const phoneInputStyle = {
  width: "100%",
  height: "38px",
  fontSize: "14px",
  backgroundColor: "#f8f9fa",
  border: "1px solid #e2e5e9",
  color: "#6c757d",
  cursor: "not-allowed",
};

const CompleteOrganisation = () => {
  const navigate = useNavigate();
  const stored = readStoredUser();
  const email = getUserDisplayEmail(stored);
  const name = getUserDisplayName(stored) || "";
  const phone = stored.user_phone || "";
  const [subCategories, setSubCategories] = useState<
    { value: string; label: string }[]
  >([]);
  const [submitting, setSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      businessName: "",
      category: { category_pid: "", category: "" } as CategoryType,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      await createOrganisation(values);
    },
  });

  useEffect(() => {
    if (getCurrentOrganisationId()) {
      navigate("/application/dashboard", { replace: true });
      return;
    }
    callApi("/organisations/categories?limit=200", { method: "GET" })
      .then((res) => setSubCategories(getSubCategoryOptions(res)))
      .catch(() => setSubCategories([]));
  }, [navigate]);

  const createOrganisation = async (values: {
    businessName: string;
    category: CategoryType;
  }) => {
    if (!phone) {
      ToastWarning("", "Phone number is missing on this account.");
      return;
    }

    setSubmitting(true);
    try {
      const created = (await callApi("/organisations", {
        method: "POST",
        data: {
          org_name: values.businessName.trim(),
          org_trade_name: values.businessName.trim(),
          category: values.category.category,
          category_pid: values.category.category_pid,
          country_code: "IN",
          org_email: email,
          business_poc: {
            name,
            email,
            designation: "Head of Business",
            phone,
          },
          technical_poc: {
            name,
            email,
            designation: "CTO",
            phone,
          },
        },
      })) as { organisation_id?: string };

      const orgId = created?.organisation_id || "";
      if (!orgId) {
        ToastWarning("", "Organisation was created but could not be opened. Sign in again.");
        return;
      }
      syncStoredOrganisationId(orgId);
      ToastSuccess("", ["Your organisation is ready."]);
      navigate("/application/dashboard", { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Could not create organisation";
      if (message.toLowerCase().includes("organisation name already exists")) {
        formik.setFieldError(
          "businessName",
          "This business name is already in use. Choose a different name."
        );
      }
      ToastWarning("", message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`min-vh-100 min-vw-100 ${authStyles.container}`}>
      <div style={{ height: "100vh" }}>
        <img src={bgImage} alt="" className={authStyles.image} />
      </div>
      <section className={authStyles.ui}>
        <nav className="navbar navbar-light sticky-top px-5 py-2">
          <span className="navbar-brand d-flex align-items-center">
            <img src={logo} alt="Zoop" height={35} style={{ objectFit: "contain" }} />
          </span>
        </nav>
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ padding: "0 120px", height: "calc(100vh - 65px)" }}
        >
          <div className="d-none d-lg-flex flex-column" style={{ width: 458, gap: 24 }}>
            <p
              style={{
                margin: 0,
                fontFamily: "'Geist', sans-serif",
                fontWeight: 900,
                fontSize: 64,
                lineHeight: "64px",
                background:
                  "linear-gradient(90deg, rgba(0, 0, 0, 1) 5%, rgba(33, 255, 145, 0.83) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Trusted by
              <br />
              500+ brands
            </p>
            <p
              style={{
                margin: 0,
                maxWidth: 458,
                fontFamily: "'Geist', sans-serif",
                fontSize: 24,
                lineHeight: "30px",
                color: "#515151",
              }}
            >
              We create digital-first solutions to solve toughest industry challenges.
            </p>
          </div>

          <form
            onSubmit={formik.handleSubmit}
            style={{ width: 500, flexShrink: 0, marginLeft: "auto" }}
          >
            <style>{`
              #complete-org-name,
              #complete-org-password,
              #complete-org-confirm-password,
              #complete-org-phone {
                color: #6c757d !important;
                -webkit-text-fill-color: #6c757d !important;
                cursor: not-allowed !important;
              }
            `}</style>
            <div className="d-flex flex-column shadow bg-white border rounded-4 px-4 py-4">
              <h2 className="text-2xl font-semibold">Personal Details</h2>
              <div className="row g-3 mb-3">
                <div className="col-md-6" style={{ cursor: "not-allowed" }}>
                  <Input
                    id="complete-org-name"
                    name="name"
                    placeholder="Name"
                    value={name}
                    disabled
                    readOnly
                    style={lockedFieldStyle}
                  />
                </div>
                <div className="col-md-6">
                  <PhoneInput
                    country="in"
                    value={phone.replace(/^\+/, "")}
                    disabled
                    disableDropdown
                    onChange={() => undefined}
                    inputProps={{
                      id: "complete-org-phone",
                      name: "phone",
                      readOnly: true,
                    }}
                    containerStyle={{ width: "100%", cursor: "not-allowed" }}
                    inputStyle={phoneInputStyle}
                    buttonStyle={{
                      borderTopLeftRadius: "4px",
                      borderBottomLeftRadius: "4px",
                      borderRight: "1px solid #e2e5e9",
                    }}
                    specialLabel=""
                    countryCodeEditable={false}
                  />
                </div>
                <div className="col-md-6">
                  <Input
                    id="complete-org-business-name"
                    name="businessName"
                    placeholder="Business Name"
                    value={formik.values.businessName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                  />
                  {formik.touched.businessName && formik.errors.businessName && (
                    <div className="text-xs text-danger d-block">
                      {formik.errors.businessName}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <CategoryDropdown
                    id="complete-org-category"
                    name="category"
                    formik={formik}
                    subCategories={subCategories}
                  />
                  {formik.touched.category && formik.errors.category && (
                    <div className="text-xs text-danger d-block">
                      {typeof formik.errors.category === "string"
                        ? formik.errors.category
                        : "Please select one category"}
                    </div>
                  )}
                </div>
              </div>

              <p className="mt-0 text-muted" style={{ fontSize: "16px" }}>
                Set Password
              </p>
              <section className="row g-3 mb-3">
                <div className="col-md-6" style={{ cursor: "not-allowed" }}>
                  <Input
                    id="complete-org-password"
                    type="password"
                    name="password"
                    placeholder="Password"
                    value="********"
                    disabled
                    readOnly
                    style={lockedFieldStyle}
                  />
                </div>
                <div className="col-md-6" style={{ cursor: "not-allowed" }}>
                  <Input
                    id="complete-org-confirm-password"
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value="********"
                    disabled
                    readOnly
                    style={lockedFieldStyle}
                  />
                </div>
              </section>
              <section className="mb-4">
                <span
                  className="fw-semibold text-muted d-block mb-2"
                  style={{ fontSize: "14px" }}
                >
                  Password Conditions:
                </span>
                <ul className="list-unstyled small mb-0" style={{ fontSize: "12px" }}>
                  {passwordCriteria.map(({ id, text }) => (
                    <li key={id} className="d-flex align-items-center gap-2 text-muted">
                      <span
                        className="d-inline-block rounded-circle bg-secondary"
                        style={{ width: 6, height: 6 }}
                      />
                      {text}
                    </li>
                  ))}
                </ul>
              </section>

              <StyledButton
                id="complete-org-submit"
                type="submit"
                disabled={submitting}
                isLoading={submitting}
                style={buttonStyles.primary.normal}
                hoverStyle={buttonStyles.primary.hover}
              >
                {submitting ? "Please wait..." : "Create organisation"}
              </StyledButton>
              <button
                {...testIdProps("complete-org-logout")}
                type="button"
                className="btn btn-link text-muted mt-2"
                onClick={() => logout({ redirectToLogin: true })}
              >
                Log out
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default CompleteOrganisation;
